import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Shield, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ROUTES = {
  profile_image: '/dashboard/vendor/profile',
  profile_picture: '/dashboard/vendor/profile',
  banner_image: '/dashboard/vendor/profile',
  description: '/dashboard/vendor/profile',
  bio: '/dashboard/vendor/profile',
  category: '/dashboard/vendor/profile',
  location: '/dashboard/vendor/profile',
  ethnicity: '/dashboard/vendor/profile',
  // Route Stripe setup to the EntrepreNatives-branded payout wizard
  stripe_connect: '/vendor/setup-payouts',
  stripe_connected: '/vendor/setup-payouts',
  first_service: '/dashboard/vendor/services/new',
  service_photos: '/dashboard/vendor/services',
  portfolio: '/dashboard/vendor/profile',
};

/** Reusable "Profile Setup" completion card.
 *  Renders nothing when the vendor's profile is 100% complete. */
export default function ProfileCompletion() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/completion/vendor`, { headers: getAuthHeaders() });
        if (res.ok) setData(await res.json());
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data || !data.is_vendor || data.percentage >= 100) return null;

  return (
    <div
      className="p-5 rounded-3xl border border-[#8B5CF6]/25 bg-[#13153A]/70 backdrop-blur-md shadow-[0_12px_40px_-18px_rgba(139,92,246,0.5)]"
      data-testid="completion-progress"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-syne font-bold flex items-center gap-2">
          <Shield size={18} className="text-[#A78BFA]" /> Profile Setup
        </h3>
        <span className="text-[#A78BFA] font-mono font-bold text-lg">{data.percentage}%</span>
      </div>
      <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-full transition-all duration-500"
          style={{ width: `${data.percentage}%` }}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(data.steps || []).map((step) => (
          <div
            key={step.id}
            onClick={() => !step.done && STEP_ROUTES[step.id] && navigate(STEP_ROUTES[step.id])}
            className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all ${
              step.done ? 'text-green-400' : 'text-[#9CA3AF] cursor-pointer hover:bg-[#8B5CF6]/10'
            } ${!step.done && step.priority === 'high' ? 'bg-[#8B5CF6]/5 border border-[#8B5CF6]/10' : ''}`}
            data-testid={`step-${step.id}`}
          >
            {step.done ? <CheckCircle size={14} className="text-green-400 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-current flex-shrink-0" />}
            <span className={step.done ? 'line-through opacity-60' : ''}>{step.label}</span>
            {!step.done && step.priority === 'high' && (
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/20 text-[#A78BFA] font-bold ml-auto">PRIORITY</span>
            )}
            {!step.done && <ArrowRight size={12} className="ml-auto text-[#64748B]" />}
          </div>
        ))}
      </div>
      <p className="text-[#64748B] text-[10px] mt-3 text-center">Complete all steps to get 3× more inquiries!</p>
    </div>
  );
}
